<template>
  <div>
    <b-card title="Relevância de Conteúdo">
      <b-button
        class="btn-sm btn-nav-link btn-nav-link-white"
        size="sm"
        variant="flat-primary"
        :to="{ name: 'admin-content-relevance-register' }"
      >
        <feather-icon icon="PlusIcon" />
        Adicionar
      </b-button>
      <b-button
        class="btn-sm btn-nav-link btn-nav-link-white"
        size="sm"
        variant="flat-primary"
        @click="reportData"
      >
        <feather-icon icon="FileTextIcon" />
        Exportar
      </b-button>

      <div class="d-flex flex-wrap align-items-end recalculate-relevance-toolbar mb-1 mt-1">
        <div class="mr-1 mb-0">
          <label class="mb-0 small" for="recalculate-from">De</label>
          <flat-pickr
            id="recalculate-from"
            v-model="recalculateFrom"
            class="form-control form-control-sm"
            :config="datePickerConfig"
            placeholder="Opcional"
          />
        </div>
        <div class="mr-1 mb-0">
          <label class="mb-0 small" for="recalculate-to">Até</label>
          <flat-pickr
            id="recalculate-to"
            v-model="recalculateTo"
            class="form-control form-control-sm"
            :config="datePickerConfig"
            placeholder="Opcional"
          />
        </div>
        <b-button
          class="btn-sm"
          size="sm"
          variant="flat-warning"
          :disabled="recalculating"
          @click="confirmRecalculateRelevance"
        >
          <feather-icon icon="RefreshCcwIcon" />
          {{ recalculating ? 'Recalculando...' : 'Recalcular Relevância da IA' }}
        </b-button>
      </div>

      <custom-g-table
        v-if="displayTable"
        :displayLoading="tableLoading"
        :fields="fields"
        :items="data"
        :hasEditButton="true"
        :hasDeleteButton="true"
        :totalRows="totalRows"
        :limit="limit"
        @changePage="onChangePage"
        @onEdit="onEdit"
        @onDelete="onDelete"
        @onSortChange="onSortChange"
        @onColumnFilter="onFilter"
      />
    </b-card>
  </div>
</template>

<script>
import { BButton, BCard } from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import CustomGTable from "@/views/custom/table/CustomGTable.vue";
import contentRelevanceService from "@/providers/contentRelevanceService";
import newsService from "@/providers/newsService";
import dropdownService from "@/providers/dropdownService";
import AppConstants from "@/appConstants";
import messageDialogHelper from "@/helpers/message-dialog-helper";
import messagesNotifyHelper from "@/helpers/messages-notify-helper";
import {
  setRouteQuery,
  buildParamsRouteQuery,
} from "@/helpers/route-query-helper";
import { onlyLatestRequest } from "@/helpers/latest-request-helper";

export default {
  props: {
    urlQuery: Object,
  },
  components: {
    BCard,
    BButton,
    CustomGTable,
    flatPickr,
  },
  data() {
    return {
      displayTable: false,
      sortField: {},
      data: [],
      totalRows: 0,
      limit: AppConstants.PAGINATOR_DEFAULT_LIMIT,
      page: 1,
      filter: {},
      tableLoading: true,
      recalculateFrom: null,
      recalculateTo: null,
      recalculating: false,
      datePickerConfig: {
        enableTime: false,
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "d/m/Y",
        allowInput: true,
      },
      fields: [
        { field: "action_buttons", label: "Ações", sortable: false },
        {
          field: "content",
          label: "Tipo de Conteúdo",
          type: "object",
          sortable: false,
          filterOptions: {
            filterValue: "",
            enabled: true,
            placeholder: "Pesquisar por tipo de conteúdo",
            filterDropdownItems: [],
          },
        },
        {
          field: "origin",
          label: "Origem",
          type: "object",
          sortable: false,
          filterOptions: {
            filterValue: "",
            enabled: true,
            placeholder: "Pesquisar por origem",
            filterDropdownItems: [],
          },
        },
        {
          field: "channel",
          label: "Canal",
          type: "object",
          sortable: false,
          filterOptions: {
            filterValue: "",
            enabled: true,
            placeholder: "Pesquisar por canal",
            filterDropdownItems: [],
          },
        },
        {
          field: "relevance",
          label: "Relevância",
          sortable: true,
          filterOptions: {
            filterValue: "",
            enabled: true,
            placeholder: "Pesquisar por relevância",
            trigger: "enter",
          },
        },
        {
          field: "dt_create",
          label: "Criação",
          type: "custom_date",
          sortable: true,
          filterOptions: {
            filterValue: "",
            enabled: true,
            placeholder: "Pesquisar por data",
            trigger: "enter",
          },
        },
        {
          field: "active",
          label: "Habilitado",
          type: "boolean",
          sortable: false,
          filterOptions: {
            filterValue: "",
            enabled: true,
            placeholder: "Pesquisar por ativo",
            filterDropdownItems: [
              { value: true, text: "Sim" },
              { value: false, text: "Não" },
            ],
          },
        },
      ],
    };
  },
  mounted() {
    this.$store.dispatch("admin/updatePageTitle", "Relevância de Conteúdo");
    this.loadDataFilter();
  },
  computed: {
    searchParams() {
      const params = buildParamsRouteQuery(this.$route.query);
      params.append("page", this.page);
      params.append("limit", this.limit);

      if (this.sortField.field) {
        params.append(`sort_${this.sortField.field}`, this.sortField.type);
      }
      return params;
    },
  },
  methods: {
    loadDataFilter() {
      Promise.all([
        dropdownService.getAllOrigin(new URLSearchParams()),
        dropdownService.getContent(new URLSearchParams()),
        dropdownService.getChannel(new URLSearchParams()),
      ])
        .then((res) => {
          const origins = res[0].map((item) => {
            return { value: item.id, text: item.name };
          });
          this.fields[
            this.fields.findIndex((item) => item.field == "origin")
          ].filterOptions.filterDropdownItems = origins;

          const contents = res[1].map((item) => {
            return { value: item.id, text: item.name };
          });
          this.fields[
            this.fields.findIndex((item) => item.field == "content")
          ].filterOptions.filterDropdownItems = contents;

          const channels = res[2].map((item) => {
            return { value: item.id, text: item.name };
          });
          this.fields[
            this.fields.findIndex((item) => item.field == "channel")
          ].filterOptions.filterDropdownItems = channels;
          this.setInitialTableFilters();
        })
        .catch((err) => console.log(err));
    },
    setInitialTableFilters() {
      Object.keys(this.urlQuery).forEach((item) => {
        this.fields[
          this.fields.findIndex((val) => val.field == item)
        ].filterOptions.filterValue = this.urlQuery[item];
      });
      this.loadData();
    },
    reportData() {
      const params = this.searchParams;
      params.append("download", true);
      contentRelevanceService
        .report(params)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");

          link.href = url;
          link.setAttribute("download", "relevancia-de-conteudo.xlsx");
          link.setAttribute("id", "link-download");

          document.body.appendChild(link);
          link.click();

          document.getElementById("link-download").remove();
        })
        .catch((err) => console.log(err));
    },
    confirmRecalculateRelevance() {
      const title = "Recalcular Relevância da IA";
      const period =
        this.recalculateFrom || this.recalculateTo
          ? `as notícias publicadas entre ${this.recalculateFrom || "o início"} e ${
              this.recalculateTo || "hoje"
            }`
          : "TODAS as notícias";
      const text = `Isso irá recalcular a relevância de ${period} com base no cadastro atual de Relevância de Conteúdo. Essa operação pode demorar bastante dependendo da quantidade de notícias. Deseja continuar?`;

      messageDialogHelper.displayConfirmMessage(title, text, () => {
        this.recalculateRelevance();
      });
    },
    recalculateRelevance() {
      const params = new URLSearchParams();
      if (this.recalculateFrom) params.append("from", this.recalculateFrom);
      if (this.recalculateTo) params.append("to", this.recalculateTo);

      this.recalculating = true;
      newsService
        .recalculateNewsRelevance(params)
        .then((res) => {
          const text = `Concluído: ${res.updated}/${res.scanned} notícias atualizadas, ${res.vectorizationChunksUpdated} trechos atualizados na busca por IA.`;
          if (res.vectorizationFailedBatches > 0) {
            messagesNotifyHelper.warningNotify(
              "Recalcular Relevância da IA",
              `${text} ${res.vectorizationFailedBatches} lote(s) não chegaram ao índice da busca por IA, rode novamente.`
            );
            return;
          }
          messagesNotifyHelper.successNotify("Recalcular Relevância da IA", text);
        })
        .catch((err) => {
          console.log(err);
          messagesNotifyHelper.errorNotify(
            "Recalcular Relevância da IA",
            "Erro ao recalcular a relevância das notícias."
          );
        })
        .finally(() => {
          this.recalculating = false;
        });
    },
    onChangePage(page) {
      this.page = page;
      this.loadData();
    },
    onFilter(data) {
      setRouteQuery(data, this.$route.query);
      this.loadData();
    },
    onClearFilter() {
      this.filter = {};
      this.loadData();
    },
    onEdit(data) {
      this.$router.push(
        `${AppConstants.NAVIGATION_PAGE_ADM_CONTENT_RELEVANCE_REGISTER}/${data._id}`
      );
    },
    onDelete(data) {
      const params = new URLSearchParams();
      contentRelevanceService
        .deleteContentRelevance(data._id, params)
        .then(() => {
          console.log("deleted");
          messageDialogHelper.displayDeleteDataMessage(() => {
            this.loadData();
          });
        })
        .catch((err) => {
          console.log(err);
        });
    },
    loadData() {
      this.tableLoading = true;
      onlyLatestRequest(this, 'loadData', contentRelevanceService.getAllContentRelevance(this.searchParams))
        .then((res) => {
          this.tableLoading = false;
          this.data = res.data;
          this.totalRows = res.totalRows;
          this.displayTable = true;
        })
        .catch((err) => {
          this.displayTable = true;
          console.log(err);
        });
    },
    onSortChange(params) {
      this.sortField = params;
      this.loadData();
    },
  },
};
</script>

<style scoped>
.recalculate-relevance-toolbar .form-control {
  width: 130px;
}
</style>
